<script setup>
import { ref, onMounted } from 'vue'

const users = ref([])
const activityLogs = ref([])
const tokens = ref([])
const selectedToken = ref(null)
const showTokenModal = ref(false)
const adminPassword = ref('')
const isVerifying = ref(false)
const verificationError = ref('')
const activeTab = ref('users')
const systemLogs = ref([])
const securityLogs = ref([])
const networkLogs = ref([])
const performanceLogs = ref([])
const activeLogTab = ref('system')

onMounted(async () => {
  // TODO: Fetch users, activity logs, and tokens from your API
  users.value = [
    { id: 1, username: 'admin', email: 'admin@example.com', lastActive: '2024-03-20 10:30:00' },
  ]
  
  activityLogs.value = [
    { 
      id: 1, 
      userId: 1, 
      username: 'admin',
      action: 'Database backup',
      timestamp: '2024-03-20 10:30:00',
      details: 'Created backup of production database'
    },
  ]

  tokens.value = [
    {
      id: 1,
      userId: 1,
      username: 'admin',
      tokenType: 'Access',
      lastUsed: '2024-03-20 09:45:00',
      expiresAt: '2024-03-21 09:45:00',
      status: 'Active',
      description: 'Admin dashboard access',
      token: 'eyJhbGciOiJIUzI1NiIs...', // Full token
      maskedToken: 'eyJhbG...zI1NiIs' // Masked version
    },
    {
      id: 2,
      userId: 1,
      username: 'admin',
      tokenType: 'API',
      lastUsed: '2024-03-19 14:30:00',
      expiresAt: '2024-03-18 14:30:00',
      status: 'Expired',
      description: 'Database backup automation',
      token: 'kjaJHGkjhg87264kjhKJH...',
      maskedToken: 'kjaJH...kjhKJH'
    }
  ]

  systemLogs.value = [
    {
      id: 1,
      timestamp: '2024-03-20 10:30:00',
      level: 'ERROR',
      service: 'nginx',
      message: 'Failed to bind to port 80',
      details: 'Address already in use'
    },
    {
      id: 2,
      timestamp: '2024-03-20 10:29:00',
      level: 'INFO',
      service: 'docker',
      message: 'Container mysql-prod started',
      details: 'Container ID: abc123'
    }
  ]

  securityLogs.value = [
    {
      id: 1,
      timestamp: '2024-03-20 10:28:00',
      level: 'WARNING',
      source: 'firewall',
      event: 'Blocked suspicious IP',
      ip: '192.168.1.100',
      details: 'Multiple failed SSH attempts'
    },
    {
      id: 2,
      timestamp: '2024-03-20 10:27:00',
      level: 'CRITICAL',
      source: 'auth',
      event: 'Unauthorized access attempt',
      ip: '10.0.0.5',
      details: 'Invalid SSL certificate'
    }
  ]

  networkLogs.value = [
    {
      id: 1,
      timestamp: '2024-03-20 10:26:00',
      type: 'PORT',
      status: 'OPEN',
      port: 443,
      service: 'HTTPS',
      lastScanned: '2024-03-20 10:25:00',
      details: 'SSL/TLS enabled'
    },
    {
      id: 2,
      timestamp: '2024-03-20 10:25:00',
      type: 'CONNECTION',
      status: 'ACTIVE',
      source: '10.0.0.5:54321',
      destination: '10.0.0.10:3306',
      protocol: 'TCP',
      details: 'MySQL connection'
    }
  ]

  performanceLogs.value = [
    {
      id: 1,
      timestamp: '2024-03-20 10:24:00',
      metric: 'CPU',
      value: '85%',
      threshold: '90%',
      service: 'mysql-prod',
      details: 'High CPU usage detected'
    },
    {
      id: 2,
      timestamp: '2024-03-20 10:23:00',
      metric: 'MEMORY',
      value: '7.5GB/8GB',
      threshold: '7.8GB',
      service: 'redis-cache',
      details: 'Approaching memory limit'
    }
  ]
})

const getTokenStatusClass = (token) => {
  const now = new Date()
  const expiryDate = new Date(token.expiresAt)
  
  if (expiryDate < now) {
    return 'bg-red-100 text-red-800'
  }
  if (expiryDate - now < 24 * 60 * 60 * 1000) {
    return 'bg-yellow-100 text-yellow-800'
  }
  return 'bg-green-100 text-green-800'
}

const viewFullToken = (token) => {
  selectedToken.value = token
  showTokenModal.value = true
  adminPassword.value = ''
  verificationError.value = ''
}

const verifyAndShowToken = async () => {
  if (!adminPassword.value) {
    verificationError.value = 'Password is required'
    return
  }

  isVerifying.value = true
  try {
    // TODO: Verify admin password with backend
    // const response = await verifyAdminPassword(adminPassword.value)
    // if (response.verified) {
    //   verificationError.value = ''
    // } else {
    //   verificationError.value = 'Invalid password'
    // }
    
    // Temporary mock verification
    await new Promise(resolve => setTimeout(resolve, 1000))
    if (adminPassword.value === 'admin') { // Replace with real verification
      verificationError.value = ''
    } else {
      verificationError.value = 'Invalid password'
    }
  } catch (error) {
    verificationError.value = 'Verification failed'
  } finally {
    isVerifying.value = false
  }
}

const closeTokenModal = () => {
  showTokenModal.value = false
  selectedToken.value = null
  adminPassword.value = ''
}

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text)
    // You could add a toast notification here
  } catch (err) {
    console.error('Failed to copy text: ', err)
  }
}

const switchTab = (tab) => {
  activeTab.value = tab
}

const getLogLevelClass = (level) => {
  const classes = {
    ERROR: 'bg-red-100 text-red-800',
    WARNING: 'bg-yellow-100 text-yellow-800',
    INFO: 'bg-blue-100 text-blue-800',
    CRITICAL: 'bg-red-200 text-red-900'
  }
  return classes[level] || 'bg-gray-100 text-gray-800'
}

const switchLogTab = (tab) => {
  activeLogTab.value = tab
}
</script>

<template>
  <div class="space-y-6">
    <div>
      <h2 class="text-2xl font-bold mb-4">User Management</h2>

      <!-- Tabs Navigation -->
      <div class="border-b border-gray-200 mb-6">
        <nav class="-mb-px flex space-x-8">
          <button
            @click="switchTab('users')"
            :class="[
              'py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap',
              activeTab === 'users'
                ? 'border-pink-500 text-pink-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            ]"
          >
            <i class="fas fa-users mr-2"></i>
            Users
          </button>

          <button
            @click="switchTab('tokens')"
            :class="[
              'py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap',
              activeTab === 'tokens'
                ? 'border-pink-500 text-pink-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            ]"
          >
            <i class="fas fa-key mr-2"></i>
            Access Tokens
          </button>

          <button
            @click="switchTab('logs')"
            :class="[
              'py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap',
              activeTab === 'logs'
                ? 'border-pink-500 text-pink-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            ]"
          >
            <i class="fas fa-history mr-2"></i>
            System Monitoring & Logs
          </button>
        </nav>
      </div>

      <!-- Users Tab -->
      <div v-if="activeTab === 'users'" class="bg-white rounded-lg shadow">
        <div class="p-4 border-b border-gray-200">
          <h3 class="text-lg font-semibold">Active Users</h3>
        </div>
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Username</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Active</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="user in users" :key="user.id">
                <td class="px-6 py-4 whitespace-nowrap">{{ user.username }}</td>
                <td class="px-6 py-4 whitespace-nowrap">{{ user.email }}</td>
                <td class="px-6 py-4 whitespace-nowrap">{{ user.lastActive }}</td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <button class="text-indigo-600 hover:text-indigo-900">Edit</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Tokens Tab -->
      <div v-if="activeTab === 'tokens'" class="bg-white rounded-lg shadow">
        <div class="p-4 border-b border-gray-200">
          <h3 class="text-lg font-semibold">Access Tokens</h3>
        </div>
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Username</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Used</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expires At</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Token</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="token in tokens" :key="token.id">
                <td class="px-6 py-4 whitespace-nowrap">{{ token.username }}</td>
                <td class="px-6 py-4 whitespace-nowrap">{{ token.tokenType }}</td>
                <td class="px-6 py-4 whitespace-nowrap">{{ token.description }}</td>
                <td class="px-6 py-4 whitespace-nowrap">{{ token.lastUsed }}</td>
                <td class="px-6 py-4 whitespace-nowrap">{{ token.expiresAt }}</td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span :class="[
                    'px-2 inline-flex text-xs leading-5 font-semibold rounded-full',
                    getTokenStatusClass(token)
                  ]">
                    {{ new Date(token.expiresAt) < new Date() ? 'Expired' : 'Active' }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center space-x-2">
                    <span class="font-mono">{{ token.maskedToken }}</span>
                    <button 
                      @click="viewFullToken(token)"
                      class="text-indigo-600 hover:text-indigo-900"
                    >
                      <i class="fas fa-eye"></i>
                    </button>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <button class="text-red-600 hover:text-red-900 mr-2">Revoke</button>
                  <button class="text-indigo-600 hover:text-indigo-900">Extend</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Activity Logs Tab -->
      <div v-if="activeTab === 'logs'" class="bg-white rounded-lg shadow">
        <div class="p-4 border-b border-gray-200">
          <h3 class="text-lg font-semibold">System Monitoring & Logs</h3>
          
          <!-- Log Type Tabs -->
          <div class="mt-4 border-b border-gray-200">
            <nav class="-mb-px flex space-x-6">
              <button
                v-for="(tab, index) in [
                  { id: 'system', icon: 'fa-server', label: 'System Logs' },
                  { id: 'security', icon: 'fa-shield-alt', label: 'Security' },
                  { id: 'network', icon: 'fa-network-wired', label: 'Network' },
                  { id: 'performance', icon: 'fa-tachometer-alt', label: 'Performance' },
                  { id: 'activity', icon: 'fa-history', label: 'User Activity' }
                ]"
                :key="index"
                @click="switchLogTab(tab.id)"
                :class="[
                  'py-2 px-3 border-b-2 text-sm font-medium whitespace-nowrap',
                  activeLogTab === tab.id
                    ? 'border-pink-500 text-pink-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                ]"
              >
                <i :class="['fas', tab.icon, 'mr-2']"></i>
                {{ tab.label }}
              </button>
            </nav>
          </div>
        </div>

        <div class="overflow-x-auto">
          <!-- System Logs -->
          <table v-if="activeLogTab === 'system'" class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Level</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="log in systemLogs" :key="log.id">
                <td class="px-6 py-4 whitespace-nowrap text-sm">{{ log.timestamp }}</td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span :class="['px-2 inline-flex text-xs leading-5 font-semibold rounded-full', getLogLevelClass(log.level)]">
                    {{ log.level }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">{{ log.service }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">{{ log.message }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ log.details }}</td>
              </tr>
            </tbody>
          </table>

          <!-- Security Logs -->
          <table v-if="activeLogTab === 'security'" class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Level</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Event</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="log in securityLogs" :key="log.id">
                <td class="px-6 py-4 whitespace-nowrap text-sm">{{ log.timestamp }}</td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span :class="['px-2 inline-flex text-xs leading-5 font-semibold rounded-full', getLogLevelClass(log.level)]">
                    {{ log.level }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">{{ log.source }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">{{ log.event }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-mono">{{ log.ip }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ log.details }}</td>
              </tr>
            </tbody>
          </table>

          <!-- Network Logs -->
          <table v-if="activeLogTab === 'network'" class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source/Port</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Destination/Service</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="log in networkLogs" :key="log.id">
                <td class="px-6 py-4 whitespace-nowrap text-sm">{{ log.timestamp }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">{{ log.type }}</td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span :class="['px-2 inline-flex text-xs leading-5 font-semibold rounded-full', 
                    log.status === 'OPEN' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800']">
                    {{ log.status }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-mono">
                  {{ log.port || log.source }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">
                  {{ log.service || log.destination }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ log.details }}</td>
              </tr>
            </tbody>
          </table>

          <!-- Performance Logs -->
          <table v-if="activeLogTab === 'performance'" class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Metric</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Threshold</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="log in performanceLogs" :key="log.id">
                <td class="px-6 py-4 whitespace-nowrap text-sm">{{ log.timestamp }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">{{ log.metric }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-mono">{{ log.value }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">{{ log.threshold }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">{{ log.service }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ log.details }}</td>
              </tr>
            </tbody>
          </table>

          <!-- User Activity Logs (existing) -->
          <table v-if="activeLogTab === 'activity'" class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Username</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="log in activityLogs" :key="log.id">
                <td class="px-6 py-4 whitespace-nowrap">{{ log.username }}</td>
                <td class="px-6 py-4 whitespace-nowrap">{{ log.action }}</td>
                <td class="px-6 py-4 whitespace-nowrap">{{ log.timestamp }}</td>
                <td class="px-6 py-4 whitespace-nowrap">{{ log.details }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>

  <!-- Token Details Modal -->
  <div v-if="showTokenModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
      <div class="flex justify-between items-center mb-4">
        <h3 class="text-lg font-semibold">View Token Details</h3>
        <button @click="closeTokenModal" class="text-gray-500 hover:text-gray-700">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div v-if="selectedToken" class="space-y-4">
        <div>
          <p class="text-sm text-gray-600">To view the full token, please enter your admin password:</p>
          <div class="mt-2">
            <input
              type="password"
              v-model="adminPassword"
              class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
              placeholder="Enter admin password"
              @keyup.enter="verifyAndShowToken"
            />
            <p v-if="verificationError" class="mt-1 text-sm text-red-600">
              {{ verificationError }}
            </p>
          </div>
        </div>

        <div v-if="!verificationError && adminPassword" class="space-y-2">
          <div class="p-3 bg-gray-50 rounded-lg">
            <div class="flex justify-between items-center">
              <span class="font-mono text-sm break-all">{{ selectedToken.token }}</span>
              <button 
                @click="copyToClipboard(selectedToken.token)"
                class="ml-2 text-indigo-600 hover:text-indigo-900"
              >
                <i class="fas fa-copy"></i>
              </button>
            </div>
          </div>
          
          <div class="text-sm text-gray-600">
            <p><strong>Type:</strong> {{ selectedToken.tokenType }}</p>
            <p><strong>Created for:</strong> {{ selectedToken.description }}</p>
            <p><strong>Last Used:</strong> {{ selectedToken.lastUsed }}</p>
            <p><strong>Expires:</strong> {{ selectedToken.expiresAt }}</p>
          </div>
        </div>

        <div class="flex justify-end space-x-3 mt-4">
          <button
            @click="verifyAndShowToken"
            class="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            :disabled="isVerifying || !adminPassword"
          >
            <span v-if="isVerifying">Verifying...</span>
            <span v-else>View Token</span>
          </button>
          <button
            @click="closeTokenModal"
            class="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template> 